import { useRef } from "react";
import { Link } from "react-router-dom";
import { hotArticles } from "@/mocks/articles";

interface Props {
  categorySlug: string;
  categoryName: string;
}

export default function CategoryArticles({ categorySlug, categoryName }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const filteredArticles = hotArticles.filter(
    (article) => article.category === categorySlug
  );

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === "left" ? -320 : 320,
        behavior: "smooth",
      });
    }
  };

  return (
    <section className="w-full bg-background-100 py-12 md:py-16">
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                {categoryName}
              </h2>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-semibold">
                精选
              </span>
            </div>
            <p className="text-sm text-foreground-500">
              {categoryName}分类下全部热门内容，持续更新中
            </p>
          </div>
          <Link
            to="/"
            className="whitespace-nowrap px-4 py-1.5 rounded-full bg-foreground-800 text-background-50 text-xs font-medium cursor-pointer hover:bg-foreground-950 transition-colors duration-200"
          >
            返回首页
          </Link>
        </div>

        <div className="relative">
          <button
            onClick={() => scroll("left")}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 rounded-full bg-background-50 border border-background-300 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors duration-200 shadow-sm"
            aria-label="向左滚动"
          >
            <i className="ri-arrow-left-s-line text-foreground-700 w-5 h-5 flex items-center justify-center"></i>
          </button>

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide px-1"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {filteredArticles.length > 0 ? (
              filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="flex-shrink-0 w-60 md:w-64 cursor-pointer group"
                >
                  <div className="bg-background-50 rounded-xl overflow-hidden">
                    <div className="h-40 md:h-44 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-3 md:p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-foreground-400">
                          {article.categoryName}
                        </span>
                        <span className="text-xs text-foreground-300">·</span>
                        <span className="text-xs text-foreground-400">
                          {article.readCount >= 10000
                            ? `${(article.readCount / 10000).toFixed(1)}万`
                            : article.readCount}{" "}
                          阅读
                        </span>
                      </div>
                      <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug mb-2 group-hover:text-accent-600 transition-colors duration-200">
                        {article.title}
                      </h3>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-400">
                          {article.publishDate}
                        </span>
                        <span className="text-lg font-bold text-accent-600">
                          {article.hotIndex >= 10000
                            ? `${(article.hotIndex / 10000).toFixed(1)}万`
                            : article.hotIndex}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="flex-shrink-0 w-full py-20 text-center">
                <p className="text-foreground-400 text-sm">该分类下暂无内容，敬请期待</p>
              </div>
            )}
          </div>

          <button
            onClick={() => scroll("right")}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 rounded-full bg-background-50 border border-background-300 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors duration-200 shadow-sm"
            aria-label="向右滚动"
          >
            <i className="ri-arrow-right-s-line text-foreground-700 w-5 h-5 flex items-center justify-center"></i>
          </button>
        </div>
      </div>
    </section>
  );
}